import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Layout/Header';
import { salaryAPI, authAPI } from '../lib/api';
import { formatCurrency, formatMonth } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Printer, Eye, Building2, AlertCircle, Download, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const MyPayslips = () => {
  const { user } = useAuth();
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('all');

  // Get unique months for dropdown
  const availableMonths = useMemo(() => {
    const months = [...new Set(salaryRecords.map(r => r.month))].filter(Boolean);
    return months.sort().reverse();
  }, [salaryRecords]);

  // Filtered records based on selected month
  const filteredRecords = useMemo(() => {
    if (selectedMonth === 'all') return salaryRecords;
    return salaryRecords.filter(r => r.month === selectedMonth);
  }, [salaryRecords, selectedMonth]);

  useEffect(() => {
    fetchPayslips();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  const fetchPayslips = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get employee info first
      const meResponse = await authAPI.getMe();
      const employee = meResponse.data.employee;
      setEmployeeData(employee);
      
      if (!employee) {
        setError('Employee profile not found. Please contact HR.');
        setLoading(false);
        return;
      }
      
      // Fetch salary records for this employee (legacy method)
      let records = [];
      try {
        const response = await salaryAPI.getAll({ employee_id: employee.id });
        records = Array.isArray(response.data) ? response.data : [];
      } catch (legacyErr) {
        console.debug('Legacy salary records not available:', legacyErr?.message);
      }
      
      // Also fetch from new batch payslips
      try {
        const batchResponse = await salaryAPI.getMyPayslips();
        const batchRecords = Array.isArray(batchResponse.data) ? batchResponse.data : [];
        // Mark batch records for identification
        batchRecords.forEach(r => { r.isBatchRecord = true; });
        records = [...records, ...batchRecords];
      } catch (batchErr) {
        console.debug('Batch payslips not available:', batchErr?.message);
      }
      
      // Sort by month descending (newest first)
      records.sort((a, b) => (b.month || '').localeCompare(a.month || ''));
      setSalaryRecords(records);
    } catch (err) {
      console.error('Error fetching payslips:', err);
      setError('Failed to load payslips. Please try again later.');
      toast.error('Failed to fetch payslips');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPayslip = async (record) => {
    try {
      let response;
      
      // Check if this is a batch record or legacy record
      if (record.isBatchRecord && record.batch_id) {
        // Use batch payslip endpoint
        response = await salaryAPI.getBatchPayslip(record.batch_id, record.id);
      } else {
        // Use legacy endpoint
        response = await salaryAPI.getPayslip(record.employee_id, record.month);
      }
      
      // The API returns data directly, not nested under 'salary'
      setSelectedPayslip(response.data);
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error fetching payslip:', err);
      toast.error('Failed to fetch payslip details');
    }
  };

  const handleDownloadPdf = async (record) => {
    try {
      if (record.isBatchRecord && record.batch_id) {
        const response = await salaryAPI.downloadPayslipPdf(record.batch_id, record.id);
        // Create blob and download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Payslip_${record.employee_code || 'EMP'}_${record.month}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Payslip downloaded');
      } else {
        toast.info('PDF download not available for this record');
      }
    } catch (err) {
      console.error('Error downloading payslip:', err);
      toast.error('Failed to download payslip');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Safe currency formatter
  const safeCurrency = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? '₹0' : formatCurrency(num);
  };

  // Safe month formatter
  const safeFormatMonth = (month) => {
    if (!month || typeof month !== 'string') return '-';
    try {
      return formatMonth(month);
    } catch {
      return month;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="my-payslips-page">
        <Header title="My Payslips" subtitle="View and download your monthly payslips" />
        <div className="p-8">
          <div className="dashboard-card text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto text-amber-400 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Unable to Load Payslips</h3>
            <p className="text-slate-500 mb-4">{error}</p>
            <Button onClick={fetchPayslips} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="my-payslips-page">
      <Header title="My Payslips" subtitle="View and download your monthly payslips" />

      <div className="p-8">
        {/* Month Filter */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Select Month:</span>
          </div>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-48" data-testid="month-selector">
              <SelectValue placeholder="All Months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {availableMonths.map(month => (
                <SelectItem key={month} value={month}>
                  {safeFormatMonth(month)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-500">
            Showing {filteredRecords.length} payslip{filteredRecords.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Payslips Grid */}
        {filteredRecords.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRecords.map((record) => (
              <div
                key={record.id}
                className="dashboard-card hover:-translate-y-1 transition-transform duration-200 cursor-pointer"
                onClick={() => handleViewPayslip(record)}
                data-testid={`payslip-card-${record.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 rounded-lg bg-slate-100">
                    <FileText className="w-6 h-6 text-slate-600" />
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    record.status === 'disbursed' ? 'bg-emerald-100 text-emerald-700' :
                    record.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {record.status?.charAt(0).toUpperCase() + record.status?.slice(1) || 'Pending'}
                  </span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">
                  {safeFormatMonth(record.month)}
                </h3>
                <div className="space-y-2 mt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Gross Salary</span>
                    <span className="font-medium">{safeCurrency(record.gross_salary || record.gross_earnings)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Deductions</span>
                    <span className="text-red-600">-{safeCurrency(record.total_deductions)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-medium text-slate-700">Net Pay</span>
                    <span className="font-semibold text-emerald-600">{safeCurrency(record.net_pay || record.net_salary)}</span>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" className="flex-1" size="sm" onClick={(e) => { e.stopPropagation(); handleViewPayslip(record); }}>
                    <Eye className="w-4 h-4 mr-2" />
                    View
                  </Button>
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleDownloadPdf(record); }}>
                    <Download className="w-4 h-4" />
                  </Button>
                  {record.isBatchRecord && (
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleDownloadPdf(record); }}>
                      <FileText className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state py-12 dashboard-card text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No payslips available</h3>
            <p className="text-slate-500">Your payslips will appear here once processed</p>
          </div>
        )}
      </div>

      {/* Payslip Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl" data-testid="payslip-modal">
          <DialogHeader className="no-print">
            <DialogTitle className="font-heading flex items-center justify-between">
              <span>Payslip - {safeFormatMonth(selectedPayslip?.month)}</span>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </DialogTitle>
          </DialogHeader>

          {selectedPayslip && (
            <div className="payslip-container" id="payslip-content">
              {/* Payslip Header */}
              <div className="payslip-header flex justify-between items-start border-b pb-4 mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-slate-900 rounded-lg flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-semibold">
                      HRMS Solution by Niyanta.io
                    </h2>
                    <p className="text-sm text-slate-500">
                      Company Address
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold">PAYSLIP</p>
                  <p className="text-sm text-slate-500">{safeFormatMonth(selectedPayslip.month)}</p>
                </div>
              </div>

              {/* Employee Details */}
              <div className="payslip-section mb-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">Employee Details</h3>
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">Name</p>
                    <p className="font-medium">{selectedPayslip.employee_name || selectedPayslip.employee?.name || employeeData?.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Employee Code</p>
                    <p className="font-medium">{selectedPayslip.employee_code || selectedPayslip.employee?.employee_code || employeeData?.employee_code || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Designation</p>
                    <p className="font-medium">{selectedPayslip.designation || selectedPayslip.employee?.designation || employeeData?.designation || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Department</p>
                    <p className="font-medium">{selectedPayslip.department || selectedPayslip.department?.name || employeeData?.department || employeeData?.department_name || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Salary Breakup */}
              <div className="payslip-section mb-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">Salary Breakup</h3>
                <div className="grid grid-cols-2 gap-8">
                  {/* Earnings */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-600 mb-2 pb-2 border-b">Earnings</h4>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b border-slate-100">
                          <td className="py-2">Basic Pay</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.basic_pay)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-2">HRA</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.hra)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-2">DA</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.da)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-2">Special Allowance</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.special_allowance)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-2">Conveyance</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.conveyance)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-2">Medical Allowance</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.medical_allowance)}</td>
                        </tr>
                        {(selectedPayslip.overtime > 0) && (
                          <tr className="border-b border-slate-100">
                            <td className="py-2">Overtime</td>
                            <td className="text-right py-2">{safeCurrency(selectedPayslip.overtime)}</td>
                          </tr>
                        )}
                        {(selectedPayslip.bonus > 0) && (
                          <tr className="border-b border-slate-100">
                            <td className="py-2">Bonus</td>
                            <td className="text-right py-2">{safeCurrency(selectedPayslip.bonus)}</td>
                          </tr>
                        )}
                        <tr className="bg-slate-100 font-semibold">
                          <td className="py-2">Gross Salary</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.gross_salary || selectedPayslip.gross_earnings)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Deductions */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-600 mb-2 pb-2 border-b">Deductions</h4>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b border-slate-100">
                          <td className="py-2">PF</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.pf_deduction)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-2">ESI</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.esi_deduction)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-2">TDS</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.tds_deduction)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-2">Professional Tax</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.professional_tax)}</td>
                        </tr>
                        {(selectedPayslip.leave_deduction > 0) && (
                          <tr className="border-b border-slate-100">
                            <td className="py-2">Leave Deduction</td>
                            <td className="text-right py-2">{safeCurrency(selectedPayslip.leave_deduction)}</td>
                          </tr>
                        )}
                        {(selectedPayslip.loan_deduction > 0) && (
                          <tr className="border-b border-slate-100">
                            <td className="py-2">Loan Deduction</td>
                            <td className="text-right py-2">{safeCurrency(selectedPayslip.loan_deduction)}</td>
                          </tr>
                        )}
                        {(selectedPayslip.other_deductions > 0) && (
                          <tr className="border-b border-slate-100">
                            <td className="py-2">Other</td>
                            <td className="text-right py-2">{safeCurrency(selectedPayslip.other_deductions)}</td>
                          </tr>
                        )}
                        <tr className="bg-slate-100 font-semibold">
                          <td className="py-2">Total Deductions</td>
                          <td className="text-right py-2">{safeCurrency(selectedPayslip.total_deductions)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="mt-6 p-4 bg-emerald-50 rounded-lg text-center">
                <p className="text-sm text-emerald-700 uppercase font-medium">Net Payable Amount</p>
                <p className="text-3xl font-heading font-bold text-emerald-700">
                  {safeCurrency(selectedPayslip.net_pay || selectedPayslip.net_salary)}
                </p>
              </div>

              {/* Footer */}
              <div className="mt-8 pt-4 border-t text-center text-xs text-slate-400">
                <p>This is a computer-generated payslip and does not require a signature.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyPayslips;
